import React, { useContext, useEffect, useState } from 'react';
import {
    BoxSkeleton,
    SkeletonLoader,
    TimelineStepProps,
    useModal,
} from '@europrocurement/flexy-components';
import {
    DossierInformations,
    DossierStepContext,
    UPDATE_CURRENT_DOSSIER_CTX,
    useActivitiesHook,
    useAdditionnalCollectionRequest,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossier,
    User,
} from '@europrocurement/l2d-modules';
import {
    BDD_SWITCH_SOCIETES_IDS_TYPE,
    setXIdSociete,
    selectedPrescripteursSelector,
    useTiersService,
    useTiersServiceSelector,
    Contact,
    useFormaliteService,
    useSociete,
    SOUS_CLIENT_SLICE_NAME,
    PRESCRIPTEUR_SLICE_NAME,
} from '@europrocurement/l2d-domain';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { TiersTiersPrescripteurReadCommerciaux } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { AxiosResponse } from 'axios';
import DossierFormValidationStatusModal from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DossierFormValidationStatusModal/DossierFormValidationStatusModal';
import { AppDispatch } from '../../../../redux/types';
import DossierProcesses from '../DossierProcesses/DossierProcesses';
import ContactSelectionModal from './fragments/ContactSelectionModal';

type CommercialInfo = {
    nom: string;
    prenom: string;
    userIdentifier: string;
    userId?: number;
};

/**
 * Onglet d'informations détaillées d'un dossier de formalité.
 *
 * Ce composant affiche les informations principales d'un dossier incluant :
 * - La timeline de traitement du dossier
 * - Les contacts assignés (avec possibilité de modification)
 * - Les formalistes assignés (avec possibilité de modification)
 * - Les informations de priorité et de mise à jour
 */
const InformationsTab: React.FunctionComponent = function () {
    const { dossier, siblings } = useCurrentDossierCtx();
    const { handleSubmitFormStatus } = useAdditionnalCollectionRequest();
    const dispatchCurrentDossier = useCurrentDossierCtxDispatch();
    const { steps } = useContext(DossierStepContext);
    const { modalActions } = useModal();

    // États de chargement pour les opérations asynchrones
    const [isUpdatingContact, setIsUpdatingContact] = useState(false);
    const [isUpdatingFormaliste, setIsUpdatingFormaliste] = useState(false);
    const [isLoadingCommercial, setIsLoadingCommercial] = useState(false);

    // États pour les données
    const [commercialInfo, setCommercialInfo] = useState<CommercialInfo | null>(null);

    const dispatch = useDispatch<AppDispatch>();
    const { enqueueSnackbar } = useSnackbar();

    // Services API
    const { dossierApi } = useFormaliteService();
    const { updateDossier } = useDossier();
    const { selectPrescripteur, prescribersApi } = useTiersService();
    const { handleAddNoteDirectly } = useActivitiesHook({});

    // Données Redux
    const xIdSociete = useSociete();

    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);

    /**
     * Transforme les étapes du processus en format compatible avec le composant Timeline
     */
    const displayedSteps: Array<TimelineStepProps> = (() => {
        let currentFound = false;
        return steps.map((step) => {
            const isCurrent = Number(step.id) === Number(dossier?.stepId);
            currentFound = currentFound || isCurrent;

            return {
                label: step.label as string,
                icon: !currentFound || isCurrent ? 'check' : undefined,
                current: isCurrent,
                id: step.id as number,
                onClick:
                    dossier && currentFound && !isCurrent
                        ? () => {
                              modalActions.call(
                                  <DossierFormValidationStatusModal
                                      dossier={dossier}
                                      withoutSaveForm
                                      handleSubmit={handleSubmitFormStatus}
                                  />,
                              );
                          }
                        : undefined,
            };
        });
    })();

    /**
     * Initialise la société associée au dossier dans le store Redux
     */
    useEffect(() => {
        dispatch(
            setXIdSociete(
                dossier?.societeId ? (dossier.societeId as BDD_SWITCH_SOCIETES_IDS_TYPE) : 12,
            ),
        );
    }, [dispatch, dossier?.societeId]);

    /**
     * Charge les informations du prescripteur associé au dossier
     */
    useEffect(() => {
        if (!dossier?.prescripteurId) return;

        dispatch({
            type: `${SOUS_CLIENT_SLICE_NAME}/deletemainSelected`,
        });
        dispatch({
            type: `${PRESCRIPTEUR_SLICE_NAME}/deletemainSelected`,
        });

        dispatch(selectPrescripteur({ id: dossier.prescripteurId }));
    }, [dispatch, dossier?.prescripteurId, selectPrescripteur]);

    /**
     * Charge les informations du commercial associé au dossier
     */
    useEffect(() => {
        const loadCommercialInfo = async () => {
            if (!dossier?.prescripteurId || !xIdSociete) {
                return;
            }

            setIsLoadingCommercial(true);

            try {
                const response: AxiosResponse =
                    await prescribersApi.getCommerciauxPrescripteurTiersItem({
                        id: dossier.prescripteurId.toString(),
                        xIdSociete,
                    });

                const commercialUsers: TiersTiersPrescripteurReadCommerciaux['commerciaux'] =
                    response?.data?.commerciaux;

                if (commercialUsers && commercialUsers.length > 0) {
                    const matchingCommercial = commercialUsers.find(
                        (commercial) => commercial.actif && commercial.domaine?.id === 3,
                    );

                    if (matchingCommercial) {
                        setCommercialInfo({
                            nom: matchingCommercial.utilisateur?.nom || '',
                            prenom: matchingCommercial.utilisateur?.prenom || '',
                            userIdentifier: matchingCommercial.utilisateur?.userIdentifier || '',
                            userId: matchingCommercial.utilisateur?.id,
                        });
                    }
                }
            } catch (error) {
                console.error('Erreur lors du chargement des informations du commercial:', error);
            } finally {
                setIsLoadingCommercial(false);
            }
        };

        loadCommercialInfo();
    }, [dossier?.prescripteurId, prescribersApi, xIdSociete]);

    /**
     * Gère l'ouverture de la modal de sélection de contact
     */
    const handleContactChange = () => {
        const contacts = prescripteur?.contacts || [];

        if (contacts.length === 0) {
            enqueueSnackbar('Aucun contact disponible pour ce prescripteur', {
                variant: 'warning',
            });
            return;
        }

        const handleContactSelection = async (newContact: Contact) => {
            try {
                setIsUpdatingContact(true);

                if (!dossier?.id || !newContact?.id) {
                    throw new Error('ID dossier ou contact manquant');
                }

                const dossierUpdated = await dossierApi.apiDossiersIdPatch({
                    id: dossier.id.toString(),
                    dossierDossierUpdatePartial: {
                        contact: newContact.id,
                    },
                });

                if (dossierUpdated && dispatchCurrentDossier) {
                    dispatchCurrentDossier({
                        type: UPDATE_CURRENT_DOSSIER_CTX,
                        payload: {
                            dossierId: dossier.id as number,
                            data: {
                                ...dossier,
                                contactNom: newContact.nomContact,
                                contactId: newContact.id,
                                contactNomComplet: `${newContact.prenomContact} ${newContact.nomContact}`,
                                contactPrenom: newContact.prenomContact,
                                contactUserIdentifier: newContact.userIdentifier || '',
                            },
                        },
                    });
                }

                modalActions.close();
                enqueueSnackbar('Contact assigné avec succès', { variant: 'success' });

                // // Recharger les données après un délai pour s'assurer de la persistance
                // setTimeout(() => {
                //     if (dossier.id) dispatch(selectOpenSearchDossier({ id: dossier.id }));
                //     refreshDossier();
                // }, 500);
            } catch (error) {
                console.error('Erreur lors de la mise à jour du contact assigné:', error);
                enqueueSnackbar('Impossible de modifier le contact assigné', {
                    variant: 'error',
                });
            } finally {
                setIsUpdatingContact(false);
            }
        };

        modalActions.call(
            <ContactSelectionModal
                contacts={contacts}
                currentContactId={dossier?.contactId?.toString()}
                onContactSelect={handleContactSelection}
                isLoading={isUpdatingContact}
            />,
            false,
            false,
        );
    };

    const handleFormalisteChange = async (
        newFormaliste: User,
        note?: string,
        applyToAll = false,
    ) => {
        try {
            setIsUpdatingFormaliste(true);

            if (!dossier?.id || !newFormaliste?.id || !dossier.contactId) {
                throw new Error('ID dossier, formaliste ou contact manquant');
            }

            const dossierIds = applyToAll
                ? [...new Set([dossier.id, ...(siblings ?? []).map((s) => s.id)])].filter(
                      (id): id is number => typeof id === 'number',
                  )
                : [dossier.id as number];

            await Promise.all(
                dossierIds.map((id) => updateDossier(id, { formaliste: newFormaliste.id })),
            );

            if (dispatchCurrentDossier) {
                dispatchCurrentDossier({
                    type: UPDATE_CURRENT_DOSSIER_CTX,
                    payload: {
                        dossierId: dossier.id as number,
                        data: {
                            ...dossier,
                            formalisteId: newFormaliste.id,
                            formalisteNom: newFormaliste.lastName,
                            formalisteNomComplet: `${newFormaliste.firstName} ${newFormaliste.lastName}`,
                            formalistePrenom: newFormaliste.firstName,
                            formalisteUserIdentifier: newFormaliste.keycloakUserIdentifier || '',
                        },
                    },
                });
            }

            enqueueSnackbar(
                applyToAll
                    ? 'Formaliste assigné à tous les dossiers de la commande'
                    : 'Formaliste assigné avec succès',
                { variant: 'success' },
            );

            if (note) {
                await handleAddNoteDirectly(note);
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du formaliste assigné:', error);
            enqueueSnackbar('Impossible de modifier le formaliste assigné', { variant: 'error' });
            throw error;
        } finally {
            setIsUpdatingFormaliste(false);
        }
    };

    const isLoading = dossier === null || dossier === undefined;

    return (
        <Box data-testid="dossier-information-tab">
            {/* Démarches associées au dossier */}
            <DossierProcesses />

            {/* Informations principales du dossier avec timeline et données de base */}
            <SkeletonLoader
                isLoading={isLoading}
                containerSx={{
                    marginTop: '10px',
                    marginBottom: '10px',
                }}
                skeleton={
                    <BoxSkeleton
                        sx={{
                            height: '250px',
                        }}
                    />
                }
            >
                <DossierInformations
                    steps={displayedSteps}
                    dossierId={dossier?.id as number}
                    contactNom={dossier?.contactNom as string}
                    contactPrenom={dossier?.contactPrenom as string}
                    contactUserIdentifier={dossier?.contactUserIdentifier as string}
                    contactId={dossier?.contactId as number}
                    commercialNom={commercialInfo?.nom || ''}
                    commercialPrenom={commercialInfo?.prenom || ''}
                    commercialUserIdentifier={commercialInfo?.userIdentifier || ''}
                    commercialId={commercialInfo?.userId}
                    formalisteId={dossier?.formalisteId as number}
                    formalisteNom={dossier?.formalisteNom as string}
                    formalistePrenom={dossier?.formalistePrenom as string}
                    formalisteUserIdentifier={dossier?.formalisteUserIdentifier as string}
                    critical={dossier?.critical as boolean}
                    dateUpdate={dossier?.dateUpdate as string}
                    prescripteurContacts={prescripteur?.contacts || []}
                    onContactChange={handleContactChange}
                    onFormalisteChange={handleFormalisteChange}
                    showAllDossiersButton={
                        !!siblings && siblings.filter((d) => d.deleted === false).length > 1
                    }
                    isUpdatingContact={isUpdatingContact}
                    isUpdatingFormaliste={isUpdatingFormaliste}
                    isLoadingCommercial={isLoadingCommercial}
                    dossier={dossier}
                    source="BACK"
                />
            </SkeletonLoader>
        </Box>
    );
};

export default InformationsTab;
